import React, { useEffect, useMemo, useRef, useState } from 'react';
import ImageWithFallback from './ImageWithFallback';
import VerifiedBadge from './VerifiedBadge';
import SignedVideo from './SignedVideo';
import { auth } from '../firebase';
import UserLink from './UserLink';

export type StoryDoc = {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  mediaURL: string;
  mediaType: 'image' | 'video';
  createdAt?: any;
  expiresAt?: any;
};

export type UserStories = {
  userId: string;
  userName: string;
  userPhoto: string;
  userVerified?: boolean;
  items: StoryDoc[];
};

interface StoriesViewerProps {
  groups: UserStories[];
  startIndex: number; // which user to start with
  onClose: () => void;
}

const IMG_DURATION_MS = 5000;

const StoriesViewer: React.FC<StoriesViewerProps> = ({ groups, startIndex, onClose }) => {
  const [userIndex, setUserIndex] = useState(startIndex);
  const [storyIndex, setStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const vidRef = useRef<HTMLVideoElement | null>(null);
  const tickRef = useRef<number | null>(null);
  const touchStart = useRef<{x:number;y:number;time:number}|null>(null);

  const current = groups[userIndex];
  const items = useMemo(() => current?.items || [], [current]);
  const currentItem = items[storyIndex];

  const next = React.useCallback(() => {
    if (!current) return;
    if (storyIndex < items.length - 1) {
      setStoryIndex(storyIndex + 1);
      setProgress(0);
    } else if (userIndex < groups.length - 1) {
      setUserIndex(userIndex + 1);
      setStoryIndex(0);
      setProgress(0);
    } else {
      onClose();
    }
  }, [current, storyIndex, items.length, userIndex, groups.length, onClose]);

  const prev = React.useCallback(() => {
    if (!current) return;
    if (storyIndex > 0) {
      setStoryIndex(storyIndex - 1);
      setProgress(0);
    } else if (userIndex > 0) {
      const prevUser = groups[userIndex - 1];
      setUserIndex(userIndex - 1);
      setStoryIndex(Math.max(0, (prevUser?.items.length || 1) - 1));
      setProgress(0);
    }
  }, [current, storyIndex, userIndex, groups]);

  // Reset on user change
  useEffect(() => { setStoryIndex(0); setProgress(0); }, [userIndex]);

  // Mark seen and persist resume when current item changes
  useEffect(() => {
    if (!currentItem) return;
    const uid = auth.currentUser?.uid || 'anon';
    try {
      // seen registry
      const key = `stories_seen_${uid}`;
      const raw = localStorage.getItem(key);
      const arr: string[] = raw ? JSON.parse(raw) : [];
      if (!arr.includes(currentItem.id)) {
        arr.push(currentItem.id);
        localStorage.setItem(key, JSON.stringify(arr));
      }
    } catch {}
    // View logging handled server-side (optional).
    try {
      // resume pointer
      const key2 = `stories_resume_${auth.currentUser?.uid || 'anon'}`;
      const payload = { userId: current?.userId, storyId: currentItem.id };
      sessionStorage.setItem(key2, JSON.stringify(payload));
    } catch {}
  }, [currentItem, currentItem?.id, currentItem?.userId, current?.userId]);

  // Auto-advance logic
  useEffect(() => {
    if (!currentItem) return;
    setProgress(0);

    if (currentItem.mediaType === 'image') {
      const start = performance.now();
      const loop = (t: number) => {
        const el = document.getElementById('stories-viewer-root');
        if (!el) return;
        const dt = t - start;
        const p = Math.min(100, (dt / IMG_DURATION_MS) * 100);
        setProgress(p);
        if (p >= 100 || !playing) {
          if (!playing) return;
          next();
          return;
        }
        tickRef.current = requestAnimationFrame(loop);
      };
      tickRef.current = requestAnimationFrame(loop);
      return () => { if (tickRef.current) cancelAnimationFrame(tickRef.current); };
    } else {
      // video: rely on 'ended' event and timeupdate for progress
      const v = vidRef.current;
      if (v) {
        const onTime = () => {
          if (!v.duration || isNaN(v.duration)) return;
          setProgress(Math.max(0, Math.min(100, (v.currentTime / v.duration) * 100)));
        };
        const onEnded = () => next();
        v.addEventListener('timeupdate', onTime);
        v.addEventListener('ended', onEnded);
        // Autoplay muted per browser policy
        v.muted = muted;
        v.playsInline = true as any;
        if (playing) { v.play().catch(() => {}); }
        return () => {
          v.removeEventListener('timeupdate', onTime);
          v.removeEventListener('ended', onEnded);
        };
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem?.id, currentItem, playing, muted, next]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') { next(); }
      if (e.key === 'ArrowLeft') { prev(); }
      if (e.key.toLowerCase() === 'escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [userIndex, storyIndex, next, prev, onClose]);

  

  // Prefetch next image
  useEffect(() => {
    const n = items[storyIndex + 1];
    if (n?.mediaType === 'image') {
      const img = new Image();
      img.src = n.mediaURL;
    }
  }, [storyIndex, items]);

  if (!currentItem) return null;

  return (
    <div id="stories-viewer-root" style={styles.overlay} onClick={onClose}>
      <div
        style={styles.viewer}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={() => setPlaying(false)}
        onMouseUp={() => setPlaying(true)}
        onTouchStart={(e) => {
          const t = e.touches[0];
          touchStart.current = { x: t.clientX, y: t.clientY, time: Date.now() };
          setPlaying(false);
        }}
        onTouchMove={(e) => {
          // prevent scroll during gesture
          if (touchStart.current) e.preventDefault();
        }}
        onTouchEnd={(e) => {
          const start = touchStart.current; touchStart.current = null;
          setPlaying(true);
          if (!start) return;
          const t = e.changedTouches[0];
          const dx = t.clientX - start.x;
          const dy = t.clientY - start.y;
          const dt = Date.now() - start.time;
          const absX = Math.abs(dx), absY = Math.abs(dy);
          const THRESH = 40;
          if (dt < 600) {
            if (absX > absY && absX > THRESH) {
              if (dx < 0) next(); else prev();
              return;
            }
            if (absY > absX && absY > THRESH && dy > 0) {
              // swipe down to close
              onClose();
              return;
            }
          }
        }}
      >
        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ImageWithFallback src={current.userPhoto} alt="" style={styles.avatar} />
            <div>
              <div style={styles.userName}>
              <UserLink userId={current.userId}>{current.userName}</UserLink>
              {current.userVerified ? <VerifiedBadge size={14} /> : null}
            </div>
              <div style={styles.timeHint}>Stories • auto-advance</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={styles.closeBtn} onClick={() => setMuted(!muted)}>{muted ? '🔇' : '🔊'}</button>
            <button style={styles.closeBtn} onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Progress segments */}
        <div style={styles.progressRow}>
          {items.map((_, idx) => (
            <div key={idx} style={{ ...styles.segment, opacity: idx <= storyIndex ? 1 : 0.35 }}>
              <div style={{ ...styles.segmentFill, width: idx < storyIndex ? '100%' : (idx === storyIndex ? `${progress}%` : '0%') }} />
            </div>
          ))}
        </div>

        {/* Media */}
        <div style={styles.stage}>
          {currentItem.mediaType === 'image' ? (
            <ImageWithFallback src={currentItem.mediaURL} alt="story" style={styles.media} />
          ) : (
            <SignedVideo ref={vidRef} src={currentItem.mediaURL} style={styles.media} autoPlay muted={muted} playsInline />
          )}
          {/* Click zones */}
          <div style={styles.leftZone} onClick={prev} />
          <div style={styles.rightZone} onClick={next} />
        </div>

        {/* Controls */}
        <div style={styles.controls}>
          <button style={styles.ctrlBtn} onClick={() => setPlaying(!playing)}>{playing ? 'Pause' : 'Play'}</button>
          <button style={styles.ctrlBtn} onClick={prev}>Prev</button>
          <button style={styles.ctrlBtn} onClick={next}>Next</button>
        </div>
      </div>
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
  viewer: { position: 'relative', width: 'min(420px, 96vw)', height: 'min(740px, 90vh)', background: '#000', borderRadius: 12, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' },
  header: { position: 'absolute', top: 8, left: 8, right: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 2 },
  avatar: { width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' },
  userName: { color: '#fff', fontWeight: 600 },
  timeHint: { color: '#ddd', fontSize: 12 },
  closeBtn: { background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '4px 8px', cursor: 'pointer' },
  progressRow: { position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', gap: 4, padding: 8, zIndex: 2 },
  segment: { flex: 1, height: 3, background: 'rgba(255,255,255,0.25)', borderRadius: 3, overflow: 'hidden' },
  segmentFill: { height: '100%', background: '#17A2A9' },
  stage: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  media: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' as const },
  leftZone: { position: 'absolute', left: 0, top: 0, bottom: 0, width: '30%', cursor: 'w-resize' },
  rightZone: { position: 'absolute', right: 0, top: 0, bottom: 0, width: '30%', cursor: 'e-resize' },
  controls: { position: 'absolute', bottom: 8, left: 8, right: 8, display: 'flex', justifyContent: 'space-between', zIndex: 2 },
  ctrlBtn: { background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' },
};

export default StoriesViewer;

